import { HarnessCapability, HarnessCatalog } from "@cohesive/api-client";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { orgAPI } from "./api";

export const harnessCapabilitiesQueryKey = ["harnessCapabilities"] as const;

/**
 * The catalog as served — the runtimes the current user may start a chat on, with the models
 * each accepts, and the harness a new chat defaults to. Server-owned: availability, the sentence
 * explaining an unavailable one, and the default all come from here.
 *
 * Cached for five minutes because the answer only moves when the user's provider credentials, the
 * harness-runtime flag or their harness preference change — those mutations invalidate it
 * directly.
 */
export function useHarnessCatalog<TData = HarnessCatalog>(
  options?: Partial<UseQueryOptions<HarnessCatalog, AxiosError, TData>>,
) {
  return useQuery<HarnessCatalog, AxiosError, TData>({
    queryKey: harnessCapabilitiesQueryKey,
    queryFn: async () => {
      return await orgAPI.harnessCapabilities.list();
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

const selectCapabilities = (catalog: HarnessCatalog) =>
  catalog.harness_capabilities;

/** The catalog's harnesses alone, in catalog order. Shares the catalog's cache entry. */
export function useHarnessCapabilities() {
  return useHarnessCatalog<HarnessCapability[]>({ select: selectCapabilities });
}
